import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import {MagnifyingGlassIcon} from "@heroicons/react/24/outline"

import Button from "../../../../../components/Button.tsx"
import {
  useAdvancedSearchSearchStartMutation,
  useAdvancedSearchSessionCreateMutation,
  useCustomElevatorPitchQuery,
} from "../../../../../queries/advancedSearch.ts"
import {rollbar} from "../../../../../services"
import requestError from "../../../../../services/requestError.tsx"
import {AAdvancedSearchFilter} from "../../../../../services/types.generated.ts"
import {EGTMEvent, emitGTMEvent} from "../../../../../utils/gtm.tsx"
import {commonTransComponents} from "../../../../../utils/i18n.tsx"
import {useAutoUpdateRef} from "../../../../../utils/ref.ts"
import {ProspectsContext} from "../../../shared/context.ts"
import {ProspectsFromLeadsFrame} from "../../ProspectsFromLeadsFrame.tsx"
import {ProspectsFromLeadsLayout} from "../../ProspectsFromLeadsLayout.tsx"
import {AdvancedLeadsContext, PFLContext} from "../context/context.ts"
import {TAdvancedLeadsContext} from "../types.ts"

function filteringContextToAdvancedSearchFilter(filteringContext: TAdvancedLeadsContext): AAdvancedSearchFilter {
  return {
    cities: filteringContext.cities.value.map(city => city.value),
    country_ids: filteringContext.countries.value.map(country => country.value),
    positions: filteringContext.positions.value.map(position => position.value),
    segments: filteringContext.segments.value.map(segment => segment.value),
    company_sizes: filteringContext.companySizes.value.map(size => size.value),
    custom_elevator_pitch: filteringContext.customElevatorPitch.value.trim() || null,
  }
}

export const AdvancedSearchFilteringStage: React.FC = () => {
  const {t} = useTranslation()

  const advancedLeadsContext = AdvancedLeadsContext.useContext()
  const advancedLeadsContextRef = useAutoUpdateRef(advancedLeadsContext)
  const {assignmentId, salesCycleId} = ProspectsContext.useContext()
  const {setIsFiltersOpen} = PFLContext.useContext()

  const createAdvancedSearchSession = useAdvancedSearchSessionCreateMutation()
  const startAdvancedSearchSearch = useAdvancedSearchSearchStartMutation()
  const customElevatorPitchQuery = useCustomElevatorPitchQuery(salesCycleId)

  // Track when user has intentionally cleared the field
  const [hasUserCleared, setHasUserCleared] = React.useState(false)

  // Watch for when the field gets cleared to empty - this means user clicked clear
  const previousValue = React.useRef(advancedLeadsContext.customElevatorPitch.value)
  React.useEffect(() => {
    // If value went from non-empty to empty, user cleared it
    if (previousValue.current.trim() && !advancedLeadsContext.customElevatorPitch.value.trim()) {
      setHasUserCleared(true)
    }
    previousValue.current = advancedLeadsContext.customElevatorPitch.value
  }, [advancedLeadsContext.customElevatorPitch.value])

  // Reset clear flag when modal reopens (on mount)
  React.useEffect(() => {
    setHasUserCleared(false)
  }, [])

  React.useEffect(() => {
    // Only populate if field is empty, we have data, AND user hasn't cleared it
    if (
      customElevatorPitchQuery.data?.custom_elevator_pitch &&
      !advancedLeadsContext.customElevatorPitch.value.trim() &&
      !hasUserCleared // Don't populate if user cleared it
    ) {
      advancedLeadsContext.customElevatorPitch.setValue(customElevatorPitchQuery.data.custom_elevator_pitch)
    }
  }, [customElevatorPitchQuery.data?.custom_elevator_pitch, advancedLeadsContext.customElevatorPitch, hasUserCleared])

  const handleStartSearch = React.useCallback(async () => {
    if (assignmentId == null) {
      return
    }

    try {
      const session = await createAdvancedSearchSession.mutateAsync.call(null, {
        assignmentId,
        filter: filteringContextToAdvancedSearchFilter(advancedLeadsContextRef.current),
      })
      if (!session) {
        rollbar.error("Failed to create advanced search session")
        toast.error(t("Prospects_ImportModal_LeadsStep_Advanced_SessionFailed"))
        return
      }

      await startAdvancedSearchSearch.mutateAsync.call(null, session.id)

      emitGTMEvent({event: EGTMEvent.LEADS_ADVANCED_SEARCH_SEARCHING})

      advancedLeadsContextRef.current.setSessionId(session.id)
      advancedLeadsContextRef.current.setStage("searching")
      setIsFiltersOpen(false)
    } catch (e) {
      requestError(e)
    }
  }, [
    advancedLeadsContextRef,
    assignmentId,
    createAdvancedSearchSession.mutateAsync,
    setIsFiltersOpen,
    startAdvancedSearchSearch.mutateAsync,
    t,
  ])

  return (
    <ProspectsFromLeadsLayout>
      <ProspectsFromLeadsFrame>
        <div className={"flex flex-col items-center gap-10"}>
          <div className={"flex flex-col items-center gap-4"}>
            <div className={"flex items-center justify-center"}>
              <MagnifyingGlassIcon className={"size-24 text-cr-blue"} style={{strokeWidth: 1}} />
            </div>
            <div className={"text-center text-cr-grey-50"}>
              <Trans
                i18nKey={"Prospects_ImportModal_LeadsStep_Advanced_Filtering_Text"}
                components={commonTransComponents}
              />
            </div>
          </div>
          <Button
            onClick={handleStartSearch}
            disabled={!advancedLeadsContext.allFilters.isActive}
            className={"h-[52px] min-w-[148px] px-8 py-4 text-lg"}
          >
            {t("Prospects_ImportModal_LeadsStep_Advanced_Filtering_Button")}
          </Button>
        </div>
      </ProspectsFromLeadsFrame>
    </ProspectsFromLeadsLayout>
  )
}
